'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useMemo } from 'react';
import { Clock, Mail, MapPin, Phone, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { HomeLogoLink } from '@/components/home-logo-link';
import { buildCompanyContactRows } from '@/sanity/lib/company-contact-rows';
import type { FooterContent, SanityLogoSet } from '@/sanity/lib/navigation';
import type { SiteSettingsContent } from '@/sanity/lib/site-settings';
import { usePathname } from 'next/navigation';
import { trackContactLinkClick } from '@/lib/analytics/contact-link-tracking';
import { trackCtaClick } from '@/lib/analytics/track-cta-click';

const contactIcons = {
  mail: Mail,
  phone: Phone,
  mapPin: MapPin,
  clock: Clock,
} satisfies Record<string, LucideIcon>;

const sectionTitleClass =
  'text-[10px] sm:text-xs font-semibold uppercase tracking-[0.14em] text-[#948B7F] dark:text-[#FCFAD7]';

const linkClass =
  'text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-50 rounded-sm';

function LogoImages({
  logos,
  alt,
  className,
}: {
  logos?: SanityLogoSet | null;
  alt: string;
  className?: string;
}) {
  const useThemeLogos = logos?.enableThemeBasedLogoAlteration;
  const baseLogo = logos?.logo?.src ? logos.logo : null;
  const lightLogo = logos?.lightLogo?.src ? logos.lightLogo : null;
  const darkLogo = logos?.darkLogo?.src ? logos.darkLogo : null;

  if (useThemeLogos && (lightLogo || darkLogo)) {
    return (
      <>
        {lightLogo ? (
          <Image
            src={lightLogo.src as string}
            alt={lightLogo.alt || alt}
            width={390}
            height={83}
            className={cn(className, darkLogo && 'dark:hidden')}
          />
        ) : null}
        {darkLogo ? (
          <Image
            src={darkLogo.src as string}
            alt={darkLogo.alt || alt}
            width={390}
            height={83}
            className={cn(className, lightLogo && 'hidden dark:block')}
          />
        ) : null}
      </>
    );
  }

  if (!baseLogo?.src) return null;

  return (
    <Image
      src={baseLogo.src}
      alt={baseLogo.alt || alt}
      width={390}
      height={83}
      className={className}
    />
  );
}

export function Footer({
  content,
  company,
}: {
  content?: FooterContent | null;
  company: SiteSettingsContent;
}) {
  const pathname = usePathname();
  const isHome = pathname === '/';
  const isTerms = pathname === '/terms-of-service';
  const isPrivacy = pathname === '/privacy-policy';
  const brandName = content?.brandName;
  const description = content?.description;
  const siteLinks = content?.siteLinks ?? [];

  const contactRows = useMemo(
    () => buildCompanyContactRows(company, content?.companyContactMethods ?? undefined),
    [company, content?.companyContactMethods],
  );

  const legalCompanyName = company.companyName?.trim() || company.brandName?.trim() || '';

  return (
    <footer className="bg-neutral-50 dark:bg-neutral-950 text-foreground dark:border-t">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 min-[1324px]:px-0 xl:pt-10">
        <div className="grid gap-6 sm:gap-10 lg:grid-cols-3 lg:gap-12 xl:gap-24">
          <div className="min-w-0">
            <HomeLogoLink
              href="/"
              className="group inline-flex focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-50 rounded-sm"
            >
              {content?.showLogo !== false ? (
                <LogoImages
                  logos={content?.logos}
                  alt={brandName ?? ''}
                  className="h-12 w-auto object-contain"
                />
              ) : null}
            </HomeLogoLink>
            {description ? (
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground font-light lg:max-w-md">
                {description}
              </p>
            ) : null}
          </div>
          {content?.showSiteMenu !== false && siteLinks.length ? (
            <nav className="min-w-0 lg:mr-8 xl:mr-14" aria-label="Footer">
              {content?.siteMenuLabel ? (
                <h2 className={sectionTitleClass}>{content.siteMenuLabel}</h2>
              ) : null}
              <ul className="mt-3 grid grid-cols-2 gap-y-1.5 md:gap-x-12 xl:gap-x-16">
                {!isHome && (
                  <li className="w-fit">
                    <Link
                      href="/"
                      className={cn(linkClass, 'whitespace-nowrap')}
                      onClick={() =>
                        trackCtaClick({ location: 'footer_nav_link', label: 'Home', href: '/' })
                      }
                    >
                      Home
                    </Link>
                  </li>
                )}
                {siteLinks.map((link) => (
                  <li key={link.href} className="w-fit">
                    <Link
                      href={!isHome ? `/${link.href}` : link.href}
                      className={cn(linkClass, 'whitespace-nowrap')}
                      onClick={() =>
                        trackCtaClick({
                          location: 'footer_nav_link',
                          label: link.label,
                          href: !isHome ? `/${link.href}` : link.href,
                        })
                      }
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ) : null}
          {content?.showContactLinks !== false && contactRows.length > 0 ? (
            <div className="min-w-0">
              {content?.contactLabel ? (
                <h2 className={sectionTitleClass}>{content.contactLabel}</h2>
              ) : null}
              <ul className="mt-3 flex flex-col gap-2">
                {contactRows.map((row) => {
                  const Icon = contactIcons[row.iconKey];
                  const inner = (
                    <span className={cn(linkClass, 'inline-flex items-center gap-2.5')}>
                      <Icon className="size-3.5 shrink-0" aria-hidden />
                      <span className="font-normal wrap-break-word whitespace-pre-line text-muted-foreground hover:text-foreground transition-all duration-300 ease-in-out">
                        {row.value}
                      </span>
                    </span>
                  );
                  return (
                    <li key={`${row.label}-${row.value}`}>
                      {row.href ? (
                        <a
                          href={row.href}
                          className="block"
                          onClick={() => {
                            if (row.href) trackContactLinkClick(row.href, 'footer');
                          }}
                        >
                          {inner}
                        </a>
                      ) : (
                        inner
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </div>
        <div className="mt-6 flex flex-col-reverse gap-3 border-t pt-3 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
          <p className="text-left lg:text-center text-[10px] text-muted-foreground xl:text-left uppercase tracking-wide text-pretty">
            {new Date().getFullYear()} {legalCompanyName ? <>&copy; {legalCompanyName} </> : null}
            All rights reserved.
          </p>
          <div className="flex flex-row flex-wrap gap-2">
            <p className="text-left lg:text-center text-[10px] text-muted-foreground xl:text-right uppercase tracking-wide">
              Site built by{' '}
              <Link
                href="https://www.metawebholdings.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-[#948B7F] underline decoration-zinc-300 underline-offset-4 transition-colors hover:text-foreground hover:decoration-zinc-400 dark:text-[#FCFAD7] dark:hover:text-white dark:hover:decoration-white dark:decoration-[#FCFAD7]/40"
                onClick={() =>
                  trackCtaClick({
                    location: 'footer_outbound_metaweb',
                    label: 'Meta Web Holdings',
                    href: 'https://www.metawebholdings.com',
                  })
                }
              >
                Meta Web Holdings
              </Link>
            </p>
            <p className="text-[10px] text-muted-foreground text-left sm:text-center xl:text-right max-[400px]:hidden">
              •
            </p>
            <div className="flex flex-row flex-wrap gap-2">
              {!isPrivacy && (
                <Link
                  href="/privacy-policy"
                  className="text-left sm:text-center text-[10px] text-muted-foreground xl:text-right uppercase tracking-wide font-medium border-b border-border/60 hover:text-foreground hover:border-border transition-all duration-300 ease-in-out"
                >
                  Privacy Policy
                </Link>
              )}
              {!isTerms && (
                <Link
                  href="/terms-of-service"
                  className="text-left sm:text-center text-[10px] text-muted-foreground xl:text-right uppercase tracking-wide font-medium border-b border-border/60 hover:text-foreground hover:border-border transition-all duration-300 ease-in-out"
                >
                  Terms of Service
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
